import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    FormControlLabel,
    Switch,
    Paper,
    useTheme,
    alpha,
    TextField,
    IconButton,
    Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useChatSession } from '../contexts/ChatSessionContext';
import { PROJECT_COLORS, FONT_COLORS_DARK, FONT_COLORS_LIGHT } from '../constants/appConstants';
import { getModeBasedColor } from '../themes/migration-helpers';
import api from '../api/api';

const ProjectSettings = ({ open, onClose, project }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { fetchProjects, isInteracting } = useChatSession();

    const [projectName, setProjectName] = useState(project?.name || '');
    const [selectedColor, setSelectedColor] = useState('');
    const [selectedFontColor, setSelectedFontColor] = useState('');
    const [contextEnabled, setContextEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize the component state when the project prop changes
    useEffect(() => {
        if (project) {
            setProjectName(project.name);
            
            // Find the color ID by value
            const colorObj = PROJECT_COLORS.find(c => c.value === project.color);
            setSelectedColor(colorObj ? colorObj.id : PROJECT_COLORS[0].id);
            
            // Set font color
            setSelectedFontColor(project.fontColor || 'default');
            
            // Set context enabled
            setContextEnabled(project.contextEnabled || false);
        }
    }, [project]);

    const handleToggleContext = (event) => {
        setContextEnabled(event.target.checked);
    };

    const handleSave = async () => {
        if (!projectName.trim()) return;
        
        try {
            setIsSaving(true);

            const colorValue = PROJECT_COLORS.find(c => c.id === selectedColor)?.value;
            
            // Update project settings
            await api.put(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${project.id}/settings`, {
                name: projectName.trim(),
                color: colorValue,
                fontColor: selectedFontColor,
                contextEnabled
            });

            // Refresh projects list to get updated settings
            await fetchProjects();

            onClose();
        } catch (error) {
            console.error('Error updating project settings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!project) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <Typography variant="h6" component="div" fontWeight="medium">
                    {t('projects.settings.title', { name: project.name })}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                <TextField
                    autoFocus
                    margin="dense"
                    label={t('projects.name')}
                    fullWidth
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder={t('projects.namePlaceholder')}
                    variant="outlined"
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    {t('projects.selectColor')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {PROJECT_COLORS.map(color => (
                        <Box
                            key={color.id}
                            onClick={() => setSelectedColor(color.id)}
                            sx={(theme) => ({
                                width: 32,
                                height: 32,
                                bgcolor: color.value,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: selectedColor === color.id ? '2px solid #fff' : 'none',
                                boxShadow: selectedColor === color.id ? `0 0 0 2px ${getModeBasedColor(theme, '#000', '#fff')}` : 'none',
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.1)'
                                }
                            })}
                        />
                    ))}
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    {t('projects.selectFontColor')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {FONT_COLORS_DARK.map(color => (
                        <Tooltip key={color.id} title={color.name}>
                            <Box
                                onClick={() => setSelectedFontColor(color.id)}
                                sx={(theme) => ({
                                    width: 32,
                                    height: 32,
                                    bgcolor: color.id === 'default' 
                                        ? getModeBasedColor(theme, '#000', '#fff') 
                                        : color.value,
                                    color: color.id === 'default' 
                                        ? getModeBasedColor(theme, '#fff', '#000') 
                                        : (theme.palette.getContrastText(color.value)),
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    border: selectedFontColor === color.id ? '2px solid #fff' : 'none',
                                    boxShadow: selectedFontColor === color.id ? `0 0 0 2px ${getModeBasedColor(theme, '#000', '#fff')}` : 'none',
                                    transition: 'transform 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        transform: 'scale(1.1)'
                                    }
                                })}
                            >
                                {color.id === 'default' ? 'Aa' : 'Aa'}
                            </Box>
                        </Tooltip>
                    ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ my: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={contextEnabled}
                                onChange={handleToggleContext}
                                disabled={true} // Disable for now, as per the original code
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2">
                                    {t('projects.settings.enableProjectContext')}
                                </Typography>
                                <Tooltip title={t('projects.settings.contextTooltip')}>
                                    <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary' }} />
                                </Tooltip>
                            </Box>
                        }
                    />
                </Box>

                {contextEnabled && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                            mb: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <InfoOutlinedIcon color="primary" sx={{ mt: 0.3 }} />
                            <Box>
                                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                    {t('projects.settings.contextFeatureTitle')}
                                </Typography>
                                <Typography variant="body2">
                                    {t('projects.settings.contextFeatureDescription')}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSaving}>
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving || !projectName.trim()}
                >
                    {isSaving ? t('saving') : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProjectSettings;